/**
 * O formato da assinatura de ingestão, definido UMA vez.
 *
 * Quem assina é o cliente (o app Android), quem confere é o Worker. Se cada
 * lado tiver a sua própria cópia da regra, um dia elas divergem por um
 * caractere e o sintoma é "401 sem motivo aparente" num aparelho que eu não
 * tenho na mão. Então a montagem da mensagem e a conversão para hexadecimal
 * moram aqui, e os dois lados importam daqui.
 *
 * O que NÃO está aqui é a verificação: ela precisa do segredo decifrado e de
 * comparação em tempo constante, e isso é assunto do servidor
 * (packages/api/src/lib/hmac.ts).
 */

const enc = new TextEncoder();

export function toHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * A mensagem assinada é `timestamp.corpo`.
 *
 * O timestamp entra DENTRO da assinatura, não ao lado dela: fosse só um
 * cabeçalho, quem capturasse a requisição trocaria o valor e reenviaria o mesmo
 * corpo amanhã, e a janela anti-replay não valeria nada.
 */
export function mensagemAssinada(timestamp: string, corpo: string): string {
  return `${timestamp}.${corpo}`;
}

/** Assina `timestamp.corpo` com HMAC-SHA256. Roda em qualquer lugar com WebCrypto. */
export async function signPayload(
  secret: string,
  body: string,
  timestamp: string,
): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(mensagemAssinada(timestamp, body)));
  return toHex(new Uint8Array(sig));
}

/** Cabeçalhos de uma requisição assinada. O segredo NÃO está entre eles, de propósito. */
export async function cabecalhosAssinados(
  tokenId: string,
  secret: string,
  corpo: string,
  agoraMs: number = Date.now(),
): Promise<Record<string, string>> {
  const timestamp = String(Math.floor(agoraMs / 1000));
  return {
    "content-type": "application/json",
    "x-mostarda-token": tokenId,
    "x-mostarda-timestamp": timestamp,
    "x-mostarda-signature": await signPayload(secret, corpo, timestamp),
  };
}
